/**
 * Capability → source state + the activate/deactivate/test actions that drive
 * the capability-first Integrations UI. Talks to sc-platform's
 * `/api/capabilities` endpoints (and the shared per-integration
 * test). Activating a source flips its `isActive` on the tenant config row, so
 * every mutation invalidates BOTH the capabilities and integrations queries.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type {
  ActivateSourceBody,
  CapabilitySourcesListResponse,
  IntegrationCategory,
  IntegrationKey,
  TenantIntegrationTestResult,
} from '../../lib/types.js';

interface ActivateVars {
  capability: IntegrationCategory;
  sourceKey: IntegrationKey;
  mode: ActivateSourceBody['mode'];
  /**
   * Which connection serves this capability. Required when the tenant has more
   * than one candidate — the server rejects the activation rather than guessing,
   * because binding to the wrong one returns nothing while looking healthy.
   */
  connectionId?: string;
}

interface CapabilitySourcesLogic {
  isLoading: boolean;
  data: CapabilitySourcesListResponse | undefined;
  error: Error | null;
  activatingKey: IntegrationKey | null;
  activate: (vars: ActivateVars) => Promise<void>;
  deactivatingKey: IntegrationKey | null;
  deactivate: (capability: IntegrationCategory, sourceKey: IntegrationKey) => Promise<void>;
  testingKey: IntegrationKey | null;
  testResult: { key: IntegrationKey; result: TenantIntegrationTestResult } | null;
  test: (key: IntegrationKey) => Promise<void>;
}

export const useCapabilitySourcesLogic = (tenantId: string | undefined): CapabilitySourcesLogic => {
  const queryClient = useQueryClient();

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.capabilities(tenantId ?? '') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.integrations(tenantId ?? '') });
  };

  const listQuery = useQuery<CapabilitySourcesListResponse>({
    queryKey: queryKeys.capabilities(tenantId ?? ''),
    queryFn: () => apiRequest<CapabilitySourcesListResponse>(`/api/capabilities`),
    enabled: Boolean(tenantId),
  });

  const activateMutation = useMutation<CapabilitySourcesListResponse, Error, ActivateVars>({
    mutationFn: ({ capability, sourceKey, mode, connectionId }) =>
      apiRequest<CapabilitySourcesListResponse>(`/api/capabilities/${capability}/sources`, {
        method: 'PUT',
        body: { sourceKey, mode, ...(connectionId ? { connectionId } : {}) },
      }),
    onSuccess: invalidate,
  });

  const deactivateMutation = useMutation<
    CapabilitySourcesListResponse,
    Error,
    { capability: IntegrationCategory; sourceKey: IntegrationKey }
  >({
    mutationFn: ({ capability, sourceKey }) =>
      apiRequest<CapabilitySourcesListResponse>(
        `/api/capabilities/${capability}/sources/${sourceKey}`,
        { method: 'DELETE' },
      ),
    onSuccess: invalidate,
  });

  const testMutation = useMutation<
    { key: IntegrationKey; result: TenantIntegrationTestResult },
    Error,
    IntegrationKey
  >({
    // A test probes the connection; it doesn't change configured/active state,
    // so it needs no cache invalidation — the result is the mutation's own data.
    mutationFn: async (key) => {
      const result = await apiRequest<TenantIntegrationTestResult>(
        `/api/integrations/${key}/test`,
        { method: 'POST' },
      );
      return { key, result };
    },
  });

  return {
    isLoading: listQuery.isLoading,
    data: listQuery.data,
    error: listQuery.error,
    activatingKey: activateMutation.isPending
      ? (activateMutation.variables?.sourceKey ?? null)
      : null,
    activate: async (vars) => {
      await activateMutation.mutateAsync(vars);
    },
    deactivatingKey: deactivateMutation.isPending
      ? (deactivateMutation.variables?.sourceKey ?? null)
      : null,
    deactivate: async (capability, sourceKey) => {
      await deactivateMutation.mutateAsync({ capability, sourceKey });
    },
    testingKey: testMutation.isPending ? (testMutation.variables ?? null) : null,
    testResult: testMutation.data ?? null,
    test: async (key) => {
      await testMutation.mutateAsync(key);
    },
  };
};
